import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useResource, useAction } from '../lib/useApi.js';
import { useAuth } from '../lib/auth.js';
import type { TemplateSummary } from '../lib/types.js';
import {
  Banner,
  Card,
  Empty,
  ErrorState,
  Field,
  Loading,
  Modal,
  Pill,
  StatusPill,
  When,
  useToast,
} from '../components/ui.js';

/** Template list: what exists, what is live, and what is waiting for review. */
export function TemplatesPage(): JSX.Element {
  const { me } = useAuth();
  const templates = useResource<TemplateSummary[]>('/api/templates');
  const [creating, setCreating] = useState(false);

  const canEdit = Boolean(me?.capabilities.canEditTemplates);
  const canApprove = Boolean(me?.capabilities.canApprove);

  const awaitingReview =
    templates.data?.filter((t) => t.latestVersion?.status === 'IN_REVIEW') ?? [];

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Templates</h1>
          <p className="sub">
            Signature designs and their version history. Publishing a version makes it available to
            IT for deployment; it does not push anything by itself.
          </p>
        </div>
        <div className="page-actions">
          {canEdit && (
            <button className="primary" onClick={() => setCreating(true)}>
              New template
            </button>
          )}
        </div>
      </header>

      <div className="page-body">
        {canApprove && awaitingReview.length > 0 && (
          <Banner tone="warn">
            <strong>
              {awaitingReview.length} version{awaitingReview.length === 1 ? '' : 's'} waiting for your
              review
            </strong>
            <div className="tiny">
              {awaitingReview.map((t) => (
                <Link key={t.id} to={`/templates/${t.id}`} style={{ marginRight: 10 }}>
                  {t.name} v{t.latestVersion?.version}
                </Link>
              ))}
            </div>
          </Banner>
        )}

        {templates.error && <ErrorState error={templates.error} onRetry={templates.reload} />}
        {templates.loading && !templates.data && <Loading rows={4} label="Loading templates" />}

        {templates.data && templates.data.length === 0 && (
          <Card>
            <Empty
              title="No templates yet"
              action={
                canEdit ? (
                  <button className="primary" onClick={() => setCreating(true)}>
                    Create the first template
                  </button>
                ) : undefined
              }
            >
              A template is a signature design plus the rules for who receives it.
              {!canEdit && ' Your role can view templates but not create them.'}
            </Empty>
          </Card>
        )}

        {templates.data && templates.data.length > 0 && (
          <Card tight>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Template</th>
                    <th>Status</th>
                    <th>Live version</th>
                    <th>Latest</th>
                    <th className="num">Rules</th>
                    <th>Updated</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {templates.data.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <Link to={`/templates/${t.id}`} className="cell-strong">
                          {t.name}
                        </Link>
                        {t.description && (
                          <div className="tiny muted" style={{ maxWidth: '46ch' }}>
                            {t.description}
                          </div>
                        )}
                      </td>
                      <td>
                        <StatusPill status={t.status} />
                      </td>
                      <td>
                        {t.publishedVersion ? (
                          <span className="row">
                            <Pill tone="ok">v{t.publishedVersion.version}</Pill>
                            <span className="tiny muted">
                              <When iso={t.publishedVersion.publishedAt} />
                            </span>
                          </span>
                        ) : (
                          <span className="muted tiny">not published</span>
                        )}
                      </td>
                      <td>
                        {t.latestVersion ? (
                          <span className="row">
                            <span className="mono tiny">v{t.latestVersion.version}</span>
                            <StatusPill status={t.latestVersion.status} />
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="num">{t.ruleCount}</td>
                      <td className="tiny muted">
                        <When iso={t.updatedAt} />
                      </td>
                      <td className="right">
                        <Link className="btn sm" to={`/templates/${t.id}`}>
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {creating && (
        <CreateTemplateModal
          existing={templates.data ?? []}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void templates.reload();
          }}
        />
      )}
    </>
  );
}

function CreateTemplateModal({
  existing,
  onClose,
  onCreated,
}: {
  existing: TemplateSummary[];
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [copyFrom, setCopyFrom] = useState('');

  const create = useAction(async () => {
    const result = await api.post<{ id: string }>('/api/templates', {
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(copyFrom ? { copyFromTemplateId: copyFrom } : {}),
    });
    toast.success('Template created', `"${name.trim()}" starts as a draft.`);
    onCreated();
    navigate(`/templates/${result.id}`);
  });

  return (
    <Modal
      title="New template"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={!name.trim() || create.pending} onClick={() => void create.run()}>
            {create.pending ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      {create.error && <ErrorState error={create.error} />}

      <Field label="Name" hint="How this appears in the template list, e.g. “UK Standard” or “Sales”.">
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field label="Description (optional)">
        <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      {existing.length > 0 && (
        <Field
          label="Start from"
          hint="Copies the design of the chosen template's live version, so a regional variant does not start from scratch."
        >
          <select value={copyFrom} onChange={(e) => setCopyFrom(e.target.value)}>
            <option value="">Blank starter</option>
            {existing.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
      )}
    </Modal>
  );
}
